import SearchApplicationClient from '@elastic/search-application-client';
import {
  createTracker,
  trackPageView,
  trackSearch,
  trackSearchClick,
} from '@elastic/behavioral-analytics-javascript-tracker';

const {
  REACT_APP_SEARCH_KEY,
  REACT_APP_ENGINE_NAME,
  REACT_APP_ENDPOINT_BASE,
  REACT_APP_SPELLING_KEY,
  REACT_APP_ES_HOST,
  REACT_APP_SPELLING_SEARCH_APP,
  REACT_APP_SUGGESTIONS_SEARCH_APP,
  REACT_APP_ANALYTICS_KEY,
  REACT_APP_ANALYTICS_ENDPOINT,
  REACT_APP_ANALYTICS_COLLECTION,
  REACT_APP_ANALYTICS_APPLICATION,
} = process.env;

var suggestions_app = REACT_APP_SUGGESTIONS_SEARCH_APP;
var spelling_app = REACT_APP_SPELLING_SEARCH_APP;
var host = REACT_APP_ES_HOST;
var apiKey = REACT_APP_SPELLING_KEY;
var app_search_search_key = REACT_APP_SEARCH_KEY;
var app_search_engine = REACT_APP_ENGINE_NAME;
var app_search_endpoint = REACT_APP_ENDPOINT_BASE;

const setApplicationVariables = (props) => {
  if (props?.suggestions_search_app) {
    suggestions_app = props?.suggestions_search_app;
  }

  if (props?.spelling_search_app) {
    spelling_app = props?.spelling_search_app;
  }

  if (props?.es_host) {
    host = props?.es_host;
  }

  if (props?.es_api_key) {
    apiKey = props?.es_api_key;
  }

  if (props?.search_key) {
    app_search_search_key = props?.search_key;
  }

  if (props?.endpoint) {
    app_search_endpoint = props?.endpoint;
  }

  if (props?.engine) {
    app_search_engine = props?.engine;
  }
};

createTracker({
  endpoint: REACT_APP_ANALYTICS_ENDPOINT,
  collectionName: REACT_APP_ANALYTICS_COLLECTION,
  apiKey: REACT_APP_ANALYTICS_KEY,
});

const trackSearchQuery = async (config, data) => {
  const totalResults = data.meta?.page?.total_results || 0;
  const searchFilters = {};
  const query = config.query;
  const page = config.page;
  const sort = Object.keys(config.sort);
  const filters = config.filters.all;
  if (filters && Object.keys(filters)) {
    // there are filters so convert to the correct format for collection  { brand:"apple" }
    // original format 	"all": 	[{"source":"CAP Website"}]
    filters.forEach((filter) => {
      Object.entries(filter).forEach(([key, value]) => {
        searchFilters[key] = value;
      });
    });
  }
  const items = [];
  data.results.forEach((result) => {
    const document = {};
    document.id = result._meta.id;
    document.index = result._meta.engine;
    const page = {};
    page.url = result.url.raw;
    items.push({ document, page });
  });

  trackSearch({
    search: {
      query: query,
      filters: searchFilters,
      page: page,
      results: {
        items: items,
        total_results: totalResults,
      },
      sort: { name: sort[0] },
      search_application: REACT_APP_ANALYTICS_APPLICATION,
    },
  });
};

const search = async (config) => {
  const searchUrl = `${app_search_endpoint}/api/as/v1/engines/${app_search_engine}/search`;

  const response = await fetch(`${searchUrl}`, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
      'kbn-xsrf': 'reporting',
      Authorization: `Bearer ${app_search_search_key}`,
    }),
    redirect: 'follow',
    body: JSON.stringify(config),
  }).catch((error) => {
    console.error(error);
  });
  const data = await response.json();
  trackSearchQuery(config, data);
  return data;
};

const suggestions = async (term, site) => {
  const suggestClient = SearchApplicationClient(suggestions_app, host, apiKey);

  const results = await suggestClient().query(term).addParameter('subsite', site).search();
  if (results && results.hits) {
    const terms = results.hits.hits.map((hit) => {
      return {
        id: hit._id,
        title_raw: hit._source.title,
        title_snippet: hit.highlight.title[0],
      };
    });

    if (results) {
      return terms;
    }
  } else {
    return null;
  }
};

const spellingSuggestion = async (term) => {
  const spellingClient = SearchApplicationClient(spelling_app, host, apiKey);
  const results = await spellingClient().query(term).search();
  if (results) {
    return results.suggest.simple_phrase;
  }
};

const trackAutoSelection = (docId, query, filter) => {
  const trackClickUrl = `${app_search_endpoint}/api/as/v1/engines/${app_search_engine}/click`;

  const params = {
    query: query,
    document_id: docId,
    tags: ['autocomplete', filter.replaceAll(' ', '_')],
  };
  fetch(`${trackClickUrl}`, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${app_search_search_key}`,
    }),
    redirect: 'follow',
    body: JSON.stringify(params),
  }).catch((error) => {
    console.error(error);
  });
  return;
};

const trackSearchClickEvent = async (query, docId, url, results, title) => {
  trackSearchClick({
    document: {
      id: docId,
      index: results.meta.engine.name,
    },
    search: {
      query: query,
    },
    page: {
      url: url,
      title: title,
    },
  });
};

const trackClick = (query, docId, requestId, site, url, results, title) => {
  const trackClickUrl = `${app_search_endpoint}/api/as/v1/engines/${app_search_engine}/click`;
  let source = 'All_Sites';
  if (site[0].data && site[0].data.length === 1) {
    source = site[0].data[0].value.replaceAll(' ', '_');
  }
  const params = {
    query: query,
    request_id: requestId,
    document_id: docId,
    tags: ['search', source],
  };
  fetch(`${trackClickUrl}`, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${app_search_search_key}`,
    }),
    redirect: 'follow',
    body: JSON.stringify(params),
  }).catch((error) => {
    console.error(error);
  });
  trackSearchClickEvent(query, docId, url, results, title);

  return;
};

export {
  search,
  suggestions,
  spellingSuggestion,
  trackAutoSelection,
  trackClick,
  createTracker,
  trackPageView,
  setApplicationVariables,
};
